import React, { useState, useEffect } from 'react';
import { api } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Store, Users, Plus, Edit, Trash2, Key, Shield, ShoppingCart, Upload, Check, FileText, Image, LayoutDashboard } from 'lucide-react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useStore } from '../context/StoreContext';

export default function Admin() {
  const navigate = useNavigate();

  const { switchStore, currentStore, setCurrentStore, setStores: setContextStores } = useStore();
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stores');

  // Store modal
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeForm, setStoreForm] = useState({
    name: '', address: '', phone: '', email: '', nipt: '', logo_url: '',
    invoice_header: '', invoice_footer: 'Faleminderit per blerjen!',
    warranty_terms: 'Garancia mbulon defektet e prodhimit.',
    modules: ['sales', 'magazina', 'klientet', 'purchases']
  });

  // User modal
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [userForm, setUserForm] = useState({
    username: '', name: '', password: '', role: 'seller', store_id: 'none', custom_permissions: []
  });

  // Invoice template modal
  const [isInvoiceTemplateOpen, setIsInvoiceTemplateOpen] = useState(false);
  const [invoiceTemplateStore, setInvoiceTemplateStore] = useState(null);
  const [invoiceTemplate, setInvoiceTemplate] = useState({
    store_name: '',
    address: '',
    phone: '',
    nipt: '',
    logo_url: '',
    header_text: 'Faturë Tatimore',
    footer_text: 'Faleminderit për blerjen!',
    show_nipt: true,
    show_phone: true,
    show_address: true,
    primary_color: '#1a1a1a',
    font_size: 'medium'
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    if (user.role === 'super_admin') {
      loadData();
    }
  }, []);

  if (user.role !== 'super_admin') {
    return <Navigate to="/" replace />;
  }

  const loadData = async () => {
    try {
      const [storesRes, usersRes] = await Promise.all([
        api.get('/stores'),
        api.get('/users')
      ]);
      setStores(storesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Gabim në ngarkimin e të dhënave');
    } finally {
      setLoading(false);
    }
  };

  // Store functions
  const handleStoreSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedStore) {
        await api.put(`/stores/${selectedStore.id}`, storeForm);
        toast.success('Dyqani u përditësua');
      } else {
        await api.post('/stores', storeForm);
        toast.success('Dyqani u krijua');
      }
      setIsStoreModalOpen(false);
      resetStoreForm();

      // Refresh local data
      const storesRes = await api.get('/stores');
      setStores(storesRes.data);

      // Update context data so changes (like theme) reflect immediately
      setContextStores(storesRes.data);

      // If we edited the currently active store, update it in context too
      if (selectedStore && currentStore?.id === selectedStore.id) {
        const updatedCurrent = storesRes.data.find(s => s.id === currentStore.id);
        if (updatedCurrent) {
          setCurrentStore(updatedCurrent);
        }
      }
    } catch (error) {
      toast.error('Gabim në ruajtje');
    }
  };

  const handleStoreDelete = async (id) => {
    if (!window.confirm('Jeni të sigurt?')) return;
    try {
      await api.delete(`/stores/${id}`);
      toast.success('Dyqani u fshi');
      loadData();
    } catch (error) {
      toast.error('Gabim në fshirje');
    }
  };

  const resetStoreForm = () => {
    setStoreForm({
      name: '', type: 'tech', address: '', phone: '', email: '', nipt: '', logo_url: '',
      invoice_header: '', invoice_footer: 'Faleminderit per blerjen!',
      warranty_terms: 'Garancia mbulon defektet e prodhimit.',
      modules: ['sales', 'magazina', 'klientet', 'purchases'],
      color_primary: '#E52B50'
    });
    setSelectedStore(null);
  };

  const openStoreEdit = (store) => {
    setSelectedStore(store);
    setStoreForm({
      name: store.name, type: store.type || 'tech', address: store.address || '', phone: store.phone || '',
      email: store.email || '', nipt: store.nipt || '', logo_url: store.logo_url || '',
      invoice_header: store.invoice_header || '',
      invoice_footer: store.invoice_footer || 'Faleminderit per blerjen!',
      warranty_terms: store.warranty_terms || '',
      modules: store.modules || ['sales', 'magazina', 'klientet', 'purchases'],
      color_primary: store.color_primary || '#E52B50'
    });
    setIsStoreModalOpen(true);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedStore) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stores/${selectedStore.id}/upload-logo`, {
        method: 'POST', body: formData
      });
      const data = await response.json();
      setStoreForm(prev => ({ ...prev, logo_url: data.logo_url }));
      toast.success('Logo u ngarkua');
      loadData();
    } catch (error) {
      toast.error('Gabim në ngarkimin e logos');
    }
  };

  // User functions
  const handleUserSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...userForm,
        store_id: userForm.store_id === 'none' ? '' : userForm.store_id,
        custom_permissions: userForm.custom_permissions || []
      };
      if (selectedUser) {
        await api.put(`/users/${selectedUser.id}`, {
          name: submitData.name,
          role: submitData.role,
          store_id: submitData.store_id,
          custom_permissions: submitData.custom_permissions
        });
        toast.success('Përdoruesi u përditësua');
      } else {
        await api.post('/users', submitData);
        toast.success('Përdoruesi u krijua');
      }
      setIsUserModalOpen(false);
      resetUserForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në ruajtje');
    }
  };

  const handleUserDelete = async (id) => {
    if (!window.confirm('Jeni të sigurt?')) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success('Përdoruesi u fshi');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në fshirje');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error('Fjalëkalimi duhet të ketë së paku 4 karaktere');
      return;
    }
    try {
      await api.put(`/users/${selectedUser.id}/reset-password?new_password=${encodeURIComponent(newPassword)}`);
      toast.success('Fjalëkalimi u rivendos');
      setIsPasswordModalOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error('Gabim');
    }
  };

  const resetUserForm = () => {
    setUserForm({ username: '', name: '', password: '', role: 'seller', store_id: 'none', custom_permissions: [] });
    setSelectedUser(null);
  };

  const openUserEdit = (user) => {
    setSelectedUser(user);
    setUserForm({
      username: user.username, name: user.name || '', password: '',
      role: user.role || 'seller', store_id: user.store_id || 'none',
      custom_permissions: user.custom_permissions || []
    });
    setIsUserModalOpen(true);
  };

  const getStoreName = (storeId) => stores.find(s => s.id === storeId)?.name || '-';

  // Invoice template functions
  const openInvoiceTemplate = (store) => {
    setInvoiceTemplateStore(store);
    // Load from localStorage or use defaults
    const savedTemplate = localStorage.getItem(`invoice_template_${store.id}`);
    if (savedTemplate) {
      setInvoiceTemplate(JSON.parse(savedTemplate));
    } else {
      setInvoiceTemplate({
        store_name: store.name || '',
        address: store.address || '',
        phone: store.phone || '',
        nipt: store.nipt || '',
        logo_url: store.logo_url || '',
        header_text: 'Faturë Tatimore',
        footer_text: store.invoice_footer || 'Faleminderit për blerjen!',
        show_nipt: true,
        show_phone: true,
        show_address: true,
        primary_color: '#1a1a1a',
        font_size: 'medium'
      });
    }
    setIsInvoiceTemplateOpen(true);
  };

  const saveInvoiceTemplate = () => {
    if (!invoiceTemplateStore) return;
    localStorage.setItem(`invoice_template_${invoiceTemplateStore.id}`, JSON.stringify(invoiceTemplate));
    toast.success('Shablloni i faturës u ruajt');
    setIsInvoiceTemplateOpen(false);
  };

  const handleTemplateLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Convert to base64
    const reader = new FileReader();
    reader.onload = (event) => {
      setInvoiceTemplate(prev => ({ ...prev, logo_url: event.target.result }));
      toast.success('Logo u ngarkua');
    };
    reader.readAsDataURL(file);
  };

  const previewInvoice = () => {
    const t = invoiceTemplate;
    const fontSizes = { small: '10px', medium: '12px', large: '14px' };
    const fontSize = fontSizes[t.font_size] || '12px';

    const previewWindow = window.open('', '_blank', 'width=450,height=700');
    previewWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Paraparje Faturë</title>
          <style>
            @page { margin: 0; size: 80mm auto; }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Courier New', monospace; padding: 10px; width: 80mm; font-size: ${fontSize}; }
            .header { text-align: center; margin-bottom: 15px; border-bottom: 1px dashed ${t.primary_color}; padding-bottom: 10px; }
            .logo { max-width: 60px; max-height: 60px; margin-bottom: 5px; }
            .header h2 { font-size: 16px; margin-bottom: 3px; color: ${t.primary_color}; }
            .header p { margin: 2px 0; font-size: 10px; }
            .header .title { font-size: 14px; font-weight: bold; margin-top: 8px; }
            .info { margin-bottom: 10px; }
            .info p { margin: 3px 0; }
            .line { border-top: 1px dashed ${t.primary_color}; margin: 10px 0; }
            .items { margin-bottom: 10px; }
            .item { display: flex; justify-content: space-between; margin: 5px 0; }
            .totals { margin-top: 10px; }
            .totals .row { display: flex; justify-content: space-between; margin: 3px 0; }
            .totals .total { font-size: 14px; font-weight: bold; margin-top: 5px; }
            .footer { text-align: center; margin-top: 15px; font-size: 11px; }
            .sample { background: #f5f5f5; padding: 5px; text-align: center; color: #999; font-size: 10px; }
          </style>
        </head>
        <body>
          <div class="sample">-- PARAPARJE --</div>
          <div class="header">
            ${t.logo_url ? `<img src="${t.logo_url}" class="logo" alt="Logo" />` : ''}
            <h2>${t.store_name || 'Emri i Dyqanit'}</h2>
            ${t.show_address && t.address ? `<p>${t.address}</p>` : ''}
            ${t.show_phone && t.phone ? `<p>Tel: ${t.phone}</p>` : ''}
            ${t.show_nipt && t.nipt ? `<p>NIPT: ${t.nipt}</p>` : ''}
            <p class="title">${t.header_text}</p>
          </div>
          
          <div class="info">
            <p><strong>Nr:</strong> INV-0001</p>
            <p><strong>Data:</strong> ${new Date().toLocaleString('sq-AL')}</p>
            <p><strong>Klienti:</strong> Klient Shembull</p>
          </div>
          
          <div class="line"></div>
          
          <div class="items">
            <div class="item"><span>Produkt 1</span><span>x2 = 5,000 Lek</span></div>
            <div class="item"><span>Produkt 2</span><span>x1 = 3,000 Lek</span></div>
          </div>
          
          <div class="line"></div>
          
          <div class="totals">
            <div class="row"><span>Nëntotal:</span><span>8,000 Lek</span></div>
            <div class="row"><span>Zbritje:</span><span>-500 Lek</span></div>
            <div class="row total"><span>TOTAL:</span><span>7,500 Lek</span></div>
            <div class="row"><span>Paguar:</span><span>7,500 Lek</span></div>
          </div>
          
          <div class="footer">
            <div class="line"></div>
            <p>${t.footer_text}</p>
          </div>
        </body>
      </html>
    `);
    previewWindow.document.close();
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-page">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="stores"><Store className="w-4 h-4 mr-2" />Dyqanet</TabsTrigger>
          <TabsTrigger value="users"><Users className="w-4 h-4 mr-2" />Përdoruesit</TabsTrigger>
        </TabsList>

        {/* Stores Tab */}
        <TabsContent value="stores">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Dyqanet</h2>
            <Button onClick={() => { resetStoreForm(); setIsStoreModalOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />Dyqan i Ri
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stores.map((store) => (
              <Card key={store.id}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    {store.logo_url ? (
                      <img src={store.logo_url} alt={store.name} className="w-12 h-12 object-contain rounded" />
                    ) : (
                      <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center">
                        <Store className="w-6 h-6 text-slate-400" />
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-lg">{store.name}</CardTitle>
                      {store.address && <p className="text-xs text-slate-500">{store.address}</p>}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {store.phone && <p className="text-sm text-slate-600">{store.phone}</p>}
                  {store.nipt && <p className="text-xs text-slate-500">NIPT: {store.nipt}</p>}
                  <div className="flex gap-2 pt-2 border-t items-center">
                    <Button size="sm" variant="ghost" onClick={() => openStoreEdit(store)} title="Modifiko"><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => openInvoiceTemplate(store)} title="Shabllon Faturë"><FileText className="w-4 h-4 text-blue-500" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleStoreDelete(store.id)} title="Fshi"><Trash2 className="w-4 h-4 text-rose-500" /></Button>

                    <Button
                      size="sm"
                      onClick={() => {
                        switchStore(store.id);
                        navigate('/');
                      }}
                      className="ml-auto"
                    >
                      <LayoutDashboard className="w-3.5 h-3.5 mr-2" />
                      Menaxho
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Përdoruesit</h2>
            <Button onClick={() => { resetUserForm(); setIsUserModalOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />Përdorues i Ri
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Përdoruesi</TableHead>
                    <TableHead>Emri</TableHead>
                    <TableHead>Roli</TableHead>
                    <TableHead>Dyqani</TableHead>
                    <TableHead className="text-right">Veprime</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.username}</TableCell>
                      <TableCell>{user.name || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={['admin', 'super_admin', 'store_admin'].includes(user.role) ? 'default' : 'secondary'}>
                          {user.role === 'super_admin' ? <><Shield className="w-3 h-3 mr-1" />Super Admin</> :
                            user.role === 'store_admin' ? <><Store className="w-3 h-3 mr-1" />Admin Dyqani</> :
                              user.role === 'admin' ? <><Shield className="w-3 h-3 mr-1" />Admin</> :
                                <><ShoppingCart className="w-3 h-3 mr-1" />Shitës</>}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500">{getStoreName(user.store_id)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedUser(user); setIsPasswordModalOpen(true); }}><Key className="w-4 h-4 text-blue-500" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => openUserEdit(user)}><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleUserDelete(user.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Store Modal */}
      <Dialog open={isStoreModalOpen} onOpenChange={setIsStoreModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedStore ? 'Modifiko Dyqanin' : 'Dyqan i Ri'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleStoreSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <Label>Emri *</Label>
                <Input value={storeForm.name} onChange={(e) => setStoreForm({ ...storeForm, name: e.target.value })} required />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label>Lloji *</Label>
                <Select value={storeForm.type || 'tech'} onValueChange={(v) => setStoreForm({ ...storeForm, type: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Zgjidh llojin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tech">Teknologji (Servis)</SelectItem>
                    <SelectItem value="boutique">Butik Modë</SelectItem>
                    <SelectItem value="general">Gjenerale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label>NIPT</Label>
                <Input value={storeForm.nipt} onChange={(e) => setStoreForm({ ...storeForm, nipt: e.target.value })} />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label>Telefon</Label>
                <Input value={storeForm.phone} onChange={(e) => setStoreForm({ ...storeForm, phone: e.target.value })} />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label>Email</Label>
                <Input type="email" value={storeForm.email} onChange={(e) => setStoreForm({ ...storeForm, email: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Adresa</Label>
                <Input value={storeForm.address} onChange={(e) => setStoreForm({ ...storeForm, address: e.target.value })} />
              </div>
            </div>


            <div className="border-t pt-4">
              <label className="text-sm font-medium mb-2 block">Modulet e Aktivizuara</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'sales', label: 'Shitjet' },
                  { id: 'magazina', label: 'Magazina' },
                  { id: 'klientet', label: 'Klientët' },
                  { id: 'purchases', label: 'Blerjet' },
                  { id: 'services', label: 'Serviset (Repairs)' },
                  { id: 'ac', label: 'Kondicionerë (AC)' },
                  { id: 'reports', label: 'Raportet' }
                ].map((mod) => (
                  <label key={mod.id} className="flex items-center gap-2 border p-2 rounded hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={storeForm.modules?.includes(mod.id)}
                      onChange={(e) => {
                        const current = storeForm.modules || [];
                        if (e.target.checked) {
                          setStoreForm({ ...storeForm, modules: [...current, mod.id] });
                        } else {
                          setStoreForm({ ...storeForm, modules: current.filter(m => m !== mod.id) });
                        }
                      }}
                      className="rounded border-slate-300 text-primary focus:ring-primary"
                    />
                    <span className="text-sm">{mod.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {selectedStore && (
              <div>
                <Label>Logo</Label>
                <div className="flex items-center gap-4 mt-2">
                  {storeForm.logo_url ? (
                    <img src={storeForm.logo_url} alt="Logo" className="w-16 h-16 object-contain border rounded" />
                  ) : (
                    <div className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center"><Store className="w-8 h-8 text-slate-400" /></div>
                  )}
                  <label className="cursor-pointer">
                    <Button type="button" variant="outline" size="sm" asChild><span><Upload className="w-4 h-4 mr-2" />Ngarko Logo</span></Button>
                    <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                  </label>
                </div>
              </div>
            )}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Shablloni i Faturës</h4>
              <div className="space-y-3">
                <div>
                  <Label>Header</Label>
                  <textarea className="w-full p-2 border rounded text-sm" value={storeForm.invoice_header} onChange={(e) => setStoreForm({ ...storeForm, invoice_header: e.target.value })} rows={2} />
                </div>
                <div>
                  <Label>Footer</Label>
                  <textarea className="w-full p-2 border rounded text-sm" value={storeForm.invoice_footer} onChange={(e) => setStoreForm({ ...storeForm, invoice_footer: e.target.value })} rows={2} />
                </div>
              </div>
            </div>
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Kushtet e Garancisë</h4>
              <textarea className="w-full p-2 border rounded text-sm" value={storeForm.warranty_terms} onChange={(e) => setStoreForm({ ...storeForm, warranty_terms: e.target.value })} rows={3} />
            </div>

            <div className="border-t pt-4">
              <Label>Ngjyra e Temës</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={storeForm.color_primary || '#E52B50'}
                  onChange={(e) => setStoreForm({ ...storeForm, color_primary: e.target.value })}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={storeForm.color_primary || '#E52B50'}
                  onChange={(e) => setStoreForm({ ...storeForm, color_primary: e.target.value })}
                  className="flex-1"
                  placeholder="#E52B50"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsStoreModalOpen(false)}>Anulo</Button>
              <Button type="submit">Ruaj</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* User Modal */}
      <Dialog open={isUserModalOpen} onOpenChange={setIsUserModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedUser ? 'Modifiko Përdoruesin' : 'Përdorues i Ri'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUserSubmit} className="space-y-4">
            <div>
              <Label>Username *</Label>
              <Input value={userForm.username} onChange={(e) => setUserForm({ ...userForm, username: e.target.value })} disabled={!!selectedUser} required />
            </div>
            <div>
              <Label>Emri</Label>
              <Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} />
            </div>
            {!selectedUser && (
              <div>
                <Label>Fjalëkalimi *</Label>
                <Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} required minLength={4} />
              </div>
            )}
            <div>
              <Label>Roli *</Label>
              <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="store_admin">Admin Dyqani</SelectItem>
                  <SelectItem value="seller">Shitës</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Dyqani</Label>
              <Select value={userForm.store_id || 'none'} onValueChange={(v) => setUserForm({ ...userForm, store_id: v })}>
                <SelectTrigger><SelectValue placeholder="Zgjidh dyqanin" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Pa dyqan</SelectItem>
                  {stores.map(store => (<SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            <div className="border-t pt-4">
              <label className="text-sm font-medium mb-2 block">Lejet Shtesë (Custom)</label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                {[
                  { id: 'sales', label: 'Shitjet' },
                  { id: 'magazina', label: 'Magazina' },
                  { id: 'klientet', label: 'Klientët' },
                  { id: 'purchases', label: 'Blerjet' },
                  { id: 'services', label: 'Serviset' },
                  { id: 'ac', label: 'Kondicionerë' },
                  { id: 'reports', label: 'Raportet' },
                  { id: 'admin', label: 'Admin Panel' }
                ].map((perm) => (
                  <label key={perm.id} className="flex items-center gap-2 border p-2 rounded hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={userForm.custom_permissions?.includes(perm.id)}
                      onChange={(e) => {
                        const current = userForm.custom_permissions || [];
                        if (e.target.checked) {
                          setUserForm({ ...userForm, custom_permissions: [...current, perm.id] });
                        } else {
                          setUserForm({ ...userForm, custom_permissions: current.filter(p => p !== perm.id) });
                        }
                      }}
                      className="rounded border-slate-300 text-primary focus:ring-primary"
                    />
                    <span className="text-xs">{perm.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">Këto leje zëvendësojnë kufizimet e rolit.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsUserModalOpen(false)}>Anulo</Button>
              <Button type="submit">Ruaj</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={isPasswordModalOpen} onOpenChange={setIsPasswordModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Rivendos Fjalëkalimin</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Përdoruesi: <strong>{selectedUser?.username}</strong></p>
            <div>
              <Label>Fjalëkalimi i Ri</Label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} minLength={4} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsPasswordModalOpen(false); setNewPassword(''); }}>Anulo</Button>
            <Button onClick={handleResetPassword}>Rivendos</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Template Modal */}
      <Dialog open={isInvoiceTemplateOpen} onOpenChange={setIsInvoiceTemplateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Shabllon Faturë - {invoiceTemplateStore?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Form Fields */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-slate-600 uppercase">Informacioni i Dyqanit</h3>

              {/* Logo Upload */}
              <div className="space-y-2">
                <Label>Logo</Label>
                <div className="flex items-center gap-3">
                  {invoiceTemplate.logo_url ? (
                    <img src={invoiceTemplate.logo_url} alt="Logo" className="w-16 h-16 object-contain border rounded" />
                  ) : (
                    <div className="w-16 h-16 bg-slate-100 border rounded flex items-center justify-center">
                      <Image className="w-6 h-6 text-slate-400" />
                    </div>
                  )}
                  <div>
                    <input type="file" accept="image/*" onChange={handleTemplateLogoUpload} className="hidden" id="template-logo" />
                    <Button variant="outline" size="sm" onClick={() => document.getElementById('template-logo')?.click()}>
                      <Upload className="w-4 h-4 mr-2" />Ngarko
                    </Button>
                    {invoiceTemplate.logo_url && (
                      <Button variant="ghost" size="sm" className="ml-2" onClick={() => setInvoiceTemplate(p => ({ ...p, logo_url: '' }))}>
                        <Trash2 className="w-4 h-4 text-rose-500" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <Label>Emri i Dyqanit</Label>
                <Input value={invoiceTemplate.store_name} onChange={(e) => setInvoiceTemplate(p => ({ ...p, store_name: e.target.value }))} />
              </div>

              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label>Adresa</Label>
                  <Input value={invoiceTemplate.address} onChange={(e) => setInvoiceTemplate(p => ({ ...p, address: e.target.value }))} />
                </div>
                <label className="flex items-center gap-1 pb-2">
                  <input type="checkbox" checked={invoiceTemplate.show_address} onChange={(e) => setInvoiceTemplate(p => ({ ...p, show_address: e.target.checked }))} />
                  <span className="text-xs">Shfaq</span>
                </label>
              </div>

              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label>Telefoni</Label>
                  <Input value={invoiceTemplate.phone} onChange={(e) => setInvoiceTemplate(p => ({ ...p, phone: e.target.value }))} />
                </div>
                <label className="flex items-center gap-1 pb-2">
                  <input type="checkbox" checked={invoiceTemplate.show_phone} onChange={(e) => setInvoiceTemplate(p => ({ ...p, show_phone: e.target.checked }))} />
                  <span className="text-xs">Shfaq</span>
                </label>
              </div>

              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label>NIPT</Label>
                  <Input value={invoiceTemplate.nipt} onChange={(e) => setInvoiceTemplate(p => ({ ...p, nipt: e.target.value }))} />
                </div>
                <label className="flex items-center gap-1 pb-2">
                  <input type="checkbox" checked={invoiceTemplate.show_nipt} onChange={(e) => setInvoiceTemplate(p => ({ ...p, show_nipt: e.target.checked }))} />
                  <span className="text-xs">Shfaq</span>
                </label>
              </div>

              <h3 className="font-semibold text-sm text-slate-600 uppercase pt-4">Përmbajtja</h3>

              <div>
                <Label>Titulli i Faturës</Label>
                <Input value={invoiceTemplate.header_text} onChange={(e) => setInvoiceTemplate(p => ({ ...p, header_text: e.target.value }))} placeholder="Faturë Tatimore" />
              </div>

              <div>
                <Label>Teksti i Fundit</Label>
                <Input value={invoiceTemplate.footer_text} onChange={(e) => setInvoiceTemplate(p => ({ ...p, footer_text: e.target.value }))} placeholder="Faleminderit për blerjen!" />
              </div>

              <h3 className="font-semibold text-sm text-slate-600 uppercase pt-4">Stili</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Ngjyra Kryesore</Label>
                  <div className="flex gap-2">
                    <Input type="color" value={invoiceTemplate.primary_color} onChange={(e) => setInvoiceTemplate(p => ({ ...p, primary_color: e.target.value }))} className="w-12 h-10 p-1" />
                    <Input value={invoiceTemplate.primary_color} onChange={(e) => setInvoiceTemplate(p => ({ ...p, primary_color: e.target.value }))} className="flex-1" />
                  </div>
                </div>
                <div>
                  <Label>Madhësia e Tekstit</Label>
                  <Select value={invoiceTemplate.font_size} onValueChange={(v) => setInvoiceTemplate(p => ({ ...p, font_size: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">I Vogël</SelectItem>
                      <SelectItem value="medium">Mesatar</SelectItem>
                      <SelectItem value="large">I Madh</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="space-y-3">
              <h3 className="font-semibold text-sm text-slate-600 uppercase">Paraparje</h3>
              <div className="border rounded-lg p-4 bg-white max-h-[500px] overflow-y-auto" style={{ fontFamily: 'Courier New, monospace', fontSize: invoiceTemplate.font_size === 'small' ? '9px' : invoiceTemplate.font_size === 'large' ? '11px' : '10px' }}>
                <div style={{ textAlign: 'center', marginBottom: '10px', borderBottom: `1px dashed ${invoiceTemplate.primary_color}`, paddingBottom: '8px' }}>
                  {invoiceTemplate.logo_url && <img src={invoiceTemplate.logo_url} alt="Logo" style={{ maxWidth: '50px', maxHeight: '50px', margin: '0 auto 5px' }} />}
                  <div style={{ fontWeight: 'bold', fontSize: '14px', color: invoiceTemplate.primary_color }}>{invoiceTemplate.store_name || 'Emri i Dyqanit'}</div>
                  {invoiceTemplate.show_address && invoiceTemplate.address && <div style={{ fontSize: '9px' }}>{invoiceTemplate.address}</div>}
                  {invoiceTemplate.show_phone && invoiceTemplate.phone && <div style={{ fontSize: '9px' }}>Tel: {invoiceTemplate.phone}</div>}
                  {invoiceTemplate.show_nipt && invoiceTemplate.nipt && <div style={{ fontSize: '9px' }}>NIPT: {invoiceTemplate.nipt}</div>}
                  <div style={{ fontWeight: 'bold', marginTop: '5px' }}>{invoiceTemplate.header_text}</div>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <div><strong>Nr:</strong> INV-0001</div>
                  <div><strong>Data:</strong> 06/01/2026, 15:30</div>
                  <div><strong>Klienti:</strong> Klient Shembull</div>
                </div>
                <div style={{ borderTop: `1px dashed ${invoiceTemplate.primary_color}`, margin: '8px 0' }}></div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Produkt 1</span><span>x2 = 5,000 Lek</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Produkt 2</span><span>x1 = 3,000 Lek</span></div>
                </div>
                <div style={{ borderTop: `1px dashed ${invoiceTemplate.primary_color}`, margin: '8px 0' }}></div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Nëntotal:</span><span>8,000 Lek</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Zbritje:</span><span>-500 Lek</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '12px', marginTop: '3px' }}><span>TOTAL:</span><span>7,500 Lek</span></div>
                </div>
                <div style={{ textAlign: 'center', marginTop: '10px', borderTop: `1px dashed ${invoiceTemplate.primary_color}`, paddingTop: '8px' }}>
                  {invoiceTemplate.footer_text}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={previewInvoice}>Hap Paraparjen</Button>
            <Button variant="outline" onClick={() => setIsInvoiceTemplateOpen(false)}>Anulo</Button>
            <Button onClick={saveInvoiceTemplate}><Check className="w-4 h-4 mr-2" />Ruaj Shabllonin</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div >
  );
}
